/**
 * Whether anything can still reach this learner, and therefore what the mail panel has to
 * tell them. F-124.
 *
 * **It is a function, in a file with no React in it, because the sentence it chooses is the
 * one obligation this feature has that nothing else enforces.** The switches themselves are
 * easy: two booleans, two rows, done. What is not easy is being honest at the moment
 * somebody turns the nudge off — a learner with no push subscription who switches it off
 * cannot be reached by this system at all, and nothing else in the product would ever say
 * so. CLAUDE.md rule 4 is that we never message anybody the system is not for; its other
 * half, the one F-122 wrote the fallback for, is that we do not quietly stop being able to
 * teach somebody. A settings screen that let that happen without a word would be the
 * quietest possible version of exactly that.
 *
 * Kept out of the component and asserted directly for the reason `packages/web`'s other
 * pure modules exist: the tests here are Node with no DOM, and a rule embedded in JSX is a
 * rule that is checked by reading it.
 *
 * **The distinction that matters is between "nobody can reach you because you said so" and
 * "nobody can reach you because your mailbox is refusing us".** Both leave the learner
 * unreachable and they want opposite responses — one is a choice to respect, the other is a
 * thing to fix — so telling somebody the first when the second is true sends them to look
 * at a switch they never touched.
 */

/** Just enough of `MailState` to decide, so a test needs no fixture of the whole thing. */
export interface ReachInput {
  /** The nudge switched off. The account-wide choice, not this browser's. */
  nudgeOptOut: boolean;
  /** Devices on the account that could be notified. Not this browser's subscription. */
  pushDevices: number;
  /** A hard bounce or a complaint against the address. Outranks every switch. */
  suppressed: boolean;
}

export type Reach =
  /** A notification can arrive. Nothing needs saying. */
  | 'push'
  /** No device, but the nudge is on: this email is the only way in. */
  | 'mail_only'
  /** No device, and the nudge is off. Nothing reaches them at all. */
  | 'nothing_switched_off'
  /** No device, and mail to the address is refused whatever the switch says. */
  | 'nothing_bouncing';

/**
 * What can still get through.
 *
 * Push is checked first and is the whole answer when it exists: a phone in another room is
 * a device we can notify, so somebody with one is not making themselves unreachable by
 * turning off an email, and saying otherwise would be a warning that is simply false. This
 * is also why the count comes from the account and not from `pushManager.getSubscription()`
 * — the browser can only answer for itself, and the question is about the person.
 *
 * Suppression is checked before the switch, because it is the stronger fact: while an
 * address is bouncing nothing is sent whatever the preferences say (`mayMail` refuses it),
 * so telling a learner their *switch* has made them unreachable would point them at the
 * wrong thing entirely — and they would turn a switch on, see no change, and conclude the
 * product is broken.
 */
export function reachFrom(state: ReachInput): Reach {
  if (state.pushDevices > 0) return 'push';
  if (state.suppressed) return 'nothing_bouncing';
  return state.nudgeOptOut ? 'nothing_switched_off' : 'mail_only';
}
